import { FC, PropsWithChildren, ReactNode, useState } from 'react'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useTheme
} from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'

import { DataCardDetails, DataCardDetailsEntry } from '../DataCardDetails/DataCardDetails'

export type DataCardProps = PropsWithChildren & {
  /** Card title */
  title: string
  /** Optional actions to display at the right side of the header */
  headerPostfixActions?: ReactNode
  /** Loading state that shows an overlay loader */
  loading?: boolean
  /** Test ID for the component */
  testId?: string
  /** Additional details metadata about the card */
  details?: DataCardDetailsEntry[]
  /** Whether the details section is open by default */
  defaultDetailsOpen?: boolean
  Sx?: {
    card?: SxProps
    header?: SxProps
  }
}

export const DataCard: FC<DataCardProps> = ({
  title,
  headerPostfixActions,
  loading,
  children,
  testId,
  details,
  defaultDetailsOpen = true,
  Sx
}) => {
  const theme = useTheme()
  const [detailsOpen, setDetailsOpen] = useState(defaultDetailsOpen)
  const hasDetails = details && details.length > 0

  return (
    <Stack
      data-testid={testId || 'table-card'}
      sx={{
        borderRadius: Number(theme.shape.borderRadius) / 2,
        boxShadow: theme.shadows[5],
        position: 'relative',
        backgroundColor:
          theme.palette.mode === 'light' ? theme.palette.background.paper : theme.palette.grey[800],
        ...Sx?.card
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor:
            theme.palette.mode === 'light' ? 'background.default' : 'background.paper',
          color: 'text.primary',
          pl: hasDetails ? 1 : 2,
          pr: 1,
          pt: 1,
          pb: 0.8,
          minHeight: theme.palette.card.header.minHeight,
          borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
          borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
          ...Sx?.header
        }}
      >
        <Stack direction="row" alignItems="center">
          {hasDetails && (
            <ConsoleTooltip title={detailsOpen ? 'Hide details' : 'Show details'}>
              <IconButton size="small" onClick={() => setDetailsOpen((p) => !p)}>
                <ChevronRightIcon
                  sx={{
                    transform: detailsOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                    transition: 'transform 0.2s ease-in-out',
                    color: 'text.primary'
                  }}
                />
              </IconButton>
            </ConsoleTooltip>
          )}

          <Typography variant="h6" fontWeight="bold" noWrap mr={2} data-testid="data-card-title">
            {title}
          </Typography>
        </Stack>

        {headerPostfixActions && (
          <Stack
            flexGrow={1}
            direction="row"
            gap={1}
            justifyContent="flex-end"
            alignItems="center"
            mt={-0.15}
          >
            {headerPostfixActions}
          </Stack>
        )}
      </Stack>

      <Box position="relative">
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(255, 255, 255, 0.7)"
            zIndex={2}
            sx={{ backdropFilter: 'blur(2px)' }}
          >
            <CircularProgress />
          </Box>
        )}

        {hasDetails && (
          <>
            <Box
              sx={{
                backgroundColor:
                  theme.palette.mode === 'light' ? 'background.default' : 'background.paper'
              }}
            >
              <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                <DataCardDetails details={details} />
              </Collapse>
            </Box>
          </>
        )}

        {children}
      </Box>
    </Stack>
  )
}
